/* eslint-disable sonarjs/no-nested-template-literals */
import { createApi } from '@reduxjs/toolkit/query/react';

import { ResponseError } from '@/types/Error';
import { SuccessResponse } from '@/types/Success';
import {
  AdminGiftCardApiResponse,
  FundedGiftCardReportApiResponse,
  GiftCardApiResponse,
  GiftCardTransactionApiResponse,
} from '@/types/Tools';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

const applicationJson = 'application/json';

export const toolsApi = createApi({
  reducerPath: 'toolsApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: 'tools' }),
  tagTypes: ['GetGiftCardTransactionsReport'],
  endpoints: builder => ({
    getGiftCardTransactionList: builder.query<
      SuccessResponse<GiftCardTransactionApiResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetGiftCardTransactionsReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<GiftCardTransactionApiResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getGiftCardTransactionDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetGiftCardTransactionsReport?downloadExcel=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getFundedGiftCardReportList: builder.query<
      SuccessResponse<FundedGiftCardReportApiResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetFundedGiftCardTransactionsReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<GiftCardTransactionApiResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getFundedGiftCardReportDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetFundedGiftCardTransactionsReport?downloadExcel=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getGiftCardList: builder.query<
      SuccessResponse<GiftCardApiResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetFilteredCard?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<GiftCardApiResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getGiftCardDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetFilteredCard?downloadExcel=true${searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    saveGiftCards: builder.mutation<
      SuccessResponse<any>, // Response type
      { CardNumber: string; SecurityCode: string; Balance: string }[]
    >({
      query: cards => ({
        url: `/savecards`,
        method: 'POST',
        body: cards,
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<any>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getAdminGiftCardList: builder.query<
      SuccessResponse<AdminGiftCardApiResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetAdminGiftCardTransactionsReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<AdminGiftCardApiResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getAdminGiftCardDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetAdminGiftCardTransactionsReport?downloadExcel=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
  }),
});

export const {
  useGetGiftCardListQuery,
  useLazyGetGiftCardDownloadUrlQuery,
  useGetGiftCardTransactionListQuery,
  useLazyGetGiftCardTransactionDownloadUrlQuery,
  useGetFundedGiftCardReportListQuery,
  useLazyGetFundedGiftCardReportDownloadUrlQuery,
  useSaveGiftCardsMutation,
  useGetAdminGiftCardListQuery,
  useLazyGetAdminGiftCardDownloadUrlQuery,
} = toolsApi;
